import {
  COV,
  FREQS,
  clamp,
  markup,
  roundTo,
} from './serviceFlavorsModel';
import { STARTUP_CATALOG } from './defaultFlavors';

const CONTRACT_SERVICE_META = [
  { role: 'Maint', key: 'maintenance', label: 'Maintenance' },
  { role: 'Irrigation', key: 'irrigation', label: 'Irrigation' },
  { role: 'Herbicide', key: 'herbicide', label: 'Herbicide' },
  { role: 'Arborist', key: 'arborist', label: 'Arborist' },
  { role: 'Fert', key: 'fertilization', label: 'Fertilization' },
];

const fiClamp = (fi) => clamp(typeof fi === 'number' ? fi : 4, 0, FREQS.length - 1);

const modeForContract = (mode) => {
  if (mode === 'in') return 'included';
  if (mode === 'extra') return 'optional';
  return 'off';
};

const detailForContract = (role, state = {}) => {
  if (role === 'Maint') {
    const days = Number(state.days) || 1;
    const trim = state.prune === 'natural' ? 'Natural pruning style' : 'Shearing trim style';
    return `${days} day${days === 1 ? '' : 's'} per week. ${trim}.`;
  }
  if (role === 'Irrigation') {
    return COV[clamp(Number(state.cov) || 0, 0, COV.length - 1)]?.note || 'Scheduled irrigation inspections and adjustments.';
  }
  if (role === 'Herbicide') return 'Pre-emergent and spot treatment service included at the selected cadence.';
  if (role === 'Arborist') {
    return `${state.trees || 0} trees and ${state.palms || 0} palms at the selected annual cadence.`;
  }
  if (role === 'Fert') return 'Fertilization visits at the selected annual cadence.';
  return '';
};

const freqForContract = (role, state = {}) => {
  if (role === 'Maint') {
    const days = Number(state.days) || 1;
    return days === 1 ? 'Weekly' : `${days}x Weekly`;
  }
  return FREQS[fiClamp(state.fi)]?.f || 'As Needed';
};

const rationaleForFlavor = (flavor) => {
  if (flavor.recommended) return 'Our recommended balance of coverage and value.';
  if (flavor.id === 'basic') return 'Essential curb appeal at the lowest monthly cost.';
  return 'Maximum coverage for a show-ready property.';
};

// Tight "best for…" line shown under each tier name on the plan spread.
const bestForFlavor = (flavor) => {
  if (flavor.recommended) return 'The balance most sites choose';
  if (flavor.id === 'basic') return 'Essential curb appeal';
  return 'Show-ready, every visit';
};

export default function buildEstimateContract({
  flavors,
  pricedById,
  globals,
  settings,
  totalSF,
  areaCount,
}) {
  return {
    version: 1,
    takeoffSummary: {
      sqft: totalSF,
      areas: areaCount,
    },
    margin: globals.margin,
    taxable: !!globals.taxable,
    taxRate: globals.taxRate,
    rates: {
      laborer: settings.laborRates?.Maint,
      irrigation: settings.laborRates?.Irrigation,
      sprayTech: settings.laborRates?.Herbicide,
      operator: settings.laborRates?.Operator ?? settings.laborRates?.Arborist,
      emergency: settings.laborRates?.Irrigation ? Number(settings.laborRates.Irrigation) * 1.5 : undefined,
      craneOperator: settings.laborRates?.['Crane Operator'],
    },
    scopeNote: 'Monthly fee includes the selected tier services. Optional services are available as add-ons and are not included in the monthly fee.',
    services: CONTRACT_SERVICE_META.map((meta) => ({
      key: meta.key,
      role: meta.role,
      name: meta.label,
      shortDescription: `${meta.label} service level by tier.`,
      byFlavor: flavors.reduce((acc, flavor) => {
        const state = flavor.svc?.[meta.role] || { mode: 'off' };
        acc[flavor.id] = {
          mode: modeForContract(state.mode),
          freq: freqForContract(meta.role, state),
          detail: detailForContract(meta.role, state),
        };
        return acc;
      }, {}),
    })),
    flavors: flavors.map((flavor) => {
      const priced = pricedById[flavor.id] || {};
      const monthly = roundTo(priced.monthly || 0, 5);
      // Itemized one-time start-up work (marked-up per item) for the proposal.
      const startupItems = STARTUP_CATALOG
        .filter((it) => flavor.startup?.[it.key]?.on)
        .map((it) => ({
          key: it.key,
          label: it.label,
          price: roundTo(markup(Number(flavor.startup[it.key].cost) || 0, globals.margin), 25),
        }));
      return {
        id: flavor.id,
        name: flavor.name,
        recommended: !!flavor.recommended,
        monthly,
        annual: roundTo(priced.price || 0, 5),
        startupOneTime: priced.startupPrice || 0,
        startupItems,
        rationale: rationaleForFlavor(flavor),
        bestFor: bestForFlavor(flavor),
      };
    }),
  };
}
